import { Visit, VisitState, VisitUiType } from './types';

import { RootState } from 'store/reducer';
import { createSelector } from 'reselect';
import { initialVisitState } from 'store/visits/reducer';
import { isFiltersPinned } from 'store/visits/util';

export const visitsState = (state: RootState): RootState['visits'] => state.visits;

export const getInvalidationToken = createSelector(
  visitsState,
  (state: RootState['visits']): number => state.invalidationToken,
);

interface PaginatedVisitsState {
  data: VisitState[];
  total: number | null;
}

type PaginatedVisitsStateSelector = (state: RootState) => PaginatedVisitsState;
export const getPaginatedVisitsState = (type: VisitUiType): PaginatedVisitsStateSelector =>
  createSelector(visitsState, (state: RootState['visits']): PaginatedVisitsState => {
    switch (type) {
      case VisitUiType.PAST:
        return {
          data: state.pastVisitsData as unknown as VisitState[],
          total: state.pastVisitsTotal,
        };
      case VisitUiType.UPCOMING:
        return {
          data: state.upcomingVisitsData as unknown as VisitState[],
          total: state.upcomingVisitsTotal,
        };
      case VisitUiType.ALL:
      default:
        return {
          data: state.allVisitsData as unknown as VisitState[],
          total: state.allVisitsTotal,
        };
    }
  });

type VisitStateSelector = (state: RootState) => VisitState;
export const getVisitState = (type: VisitUiType, index: number): VisitStateSelector =>
  createSelector(
    getPaginatedVisitsState(type),
    (state: PaginatedVisitsState): VisitState => (state.data[index] as VisitState) ?? initialVisitState,
  );

export const getVisitStateById = (visitId: string): VisitStateSelector =>
  createSelector(
    visitsState,
    ({ mappedData }: RootState['visits']): VisitState =>
      (mappedData[visitId] as unknown as VisitState) ?? initialVisitState,
  );

type ConfirmationVisitSelector = (state: RootState) => Visit | null;
export const getConfirmationVisit = (visitId: string): ConfirmationVisitSelector =>
  createSelector(
    visitsState,
    ({ confirmationCache }: RootState['visits']): Visit | null => confirmationCache[visitId] ?? null,
  );

export const createVisitLoadingState = createSelector(
  visitsState,
  (state: RootState['visits']): boolean => state.createVisitQueryState.loading,
);

export const getSearchFilter = createSelector(
  visitsState,
  (state: RootState['visits']): string => state.filters.search,
);

export const getStatusFilter = createSelector(visitsState, (state: RootState['visits']) => state.filters.status);

export const getCompanyFilter = createSelector(visitsState, (state: RootState['visits']) => state.filters.companyUuids);

export const getHostFilter = createSelector(visitsState, (state: RootState['visits']) => state.filters.hostUuids);

export const getCompanyOptions = createSelector(
  visitsState,
  (state: RootState['visits']) => state.filters.companyOptions,
);

export const getHostOptions = createSelector(visitsState, (state: RootState['visits']) => state.filters.hostOptions);

export const getAllFilters = createSelector(visitsState, (state: RootState['visits']) => state.filters);

export const getFiltersPinned = createSelector(visitsState, () => isFiltersPinned());

export const getVisitsLoading = createSelector(visitsState, (state: RootState['visits']): boolean => state.loading);

export const getVisitSeriesState = createSelector(visitsState, (state: RootState['visits']) => state.visitSeries);
